import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Loading from '@/components/Loading';
import FormLabel from '@/components/auth/FormLabel';
import FormLayout from '@/components/auth/FormLayout';

type ResetStatus = 'validating' | 'invalid' | 'form' | 'completed';

export default function PasswordResetTemplate({ token }: { token: string }) {
  const [status, setStatus] = useState<ResetStatus>('validating');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`/account/api/password_resets/${token}`);
        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'トークンが無効または期限切れです');
          setStatus('invalid');
          return;
        }

        setStatus('form');
      } catch {
        setError('通信エラーが発生しました');
        setStatus('invalid');
      }
    };

    void validateToken();
  }, [token]);

  const canSubmit = useMemo(() => {
    return password.trim().length > 0 && passwordConfirmation.trim().length > 0;
  }, [password, passwordConfirmation]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('パスワードが一致しません');
      return;
    }

    setIsSubmitting(true);

    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content;
      const response = await fetch('/account/api/password_resets', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
        },
        body: JSON.stringify({
          token,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'パスワードの再設定に失敗しました');
        return;
      }

      setStatus('completed');
    } catch {
      setError('通信エラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormLayout>
      <div className="flex size-full flex-col items-center justify-center">
        <div className="mb-8 text-center">
          <img src="/agent/product-logo.png" alt="Agent Hub Logo" className="h-10 w-auto" />
        </div>

        <div className="w-full max-w-[580px] rounded-xl p-8">
          {status === 'validating' && (
            <div className="flex flex-col items-center text-center">
              <Loading />
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center">
              <h1 className="text-center text-2xl leading-tight font-bold text-black">
                リンクが無効です
              </h1>
              <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error || 'トークンが無効または期限切れです'}
              </p>
            </div>
          )}

          {status === 'form' && (
            <>
              <h1 className="text-center text-3xl leading-tight font-bold text-black">
                パスワードの再設定
              </h1>
              <p className="mt-6 text-center text-sm leading-6 text-black/70">
                新しいパスワードを入力してください。パスワードは8文字以上、英数字を含めることを推奨します。
              </p>

              {error && (
                <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}

              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div>
                  <FormLabel htmlFor="new-password">新しいパスワード</FormLabel>
                  <div className="relative mt-2">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="8文字以上で入力してください"
                      className="w-full pr-10"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 bg-white"
                      aria-label={
                        showPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'
                      }
                    >
                      {showPassword ? (
                        <img
                          src="/agent/icon-eye-visible.svg"
                          alt="パスワードを非表示にする"
                          width="24"
                          height="24"
                        />
                      ) : (
                        <img
                          src="/agent/icon-eye-hidden.svg"
                          alt="パスワードを表示する"
                          width="24"
                          height="24"
                        />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <FormLabel htmlFor="new-password-confirmation">
                    新しいパスワード（確認）
                  </FormLabel>
                  <div className="relative mt-2">
                    <Input
                      id="new-password-confirmation"
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      value={passwordConfirmation}
                      onChange={(event) => setPasswordConfirmation(event.target.value)}
                      placeholder="もう一度入力してください"
                      className="w-full pr-10"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirmation((current) => !current)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 bg-white"
                      aria-label={
                        showPasswordConfirmation
                          ? 'パスワードを非表示にする'
                          : 'パスワードを表示する'
                      }
                    >
                      {showPasswordConfirmation ? (
                        <img
                          src="/agent/icon-eye-visible.svg"
                          alt="パスワードを非表示にする"
                          width="24"
                          height="24"
                        />
                      ) : (
                        <img
                          src="/agent/icon-eye-hidden.svg"
                          alt="パスワードを表示する"
                          width="24"
                          height="24"
                        />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  full
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? '設定中...' : 'パスワードを設定'}
                </Button>
              </form>
            </>
          )}

          {status === 'completed' && (
            <div className="text-center">
              <h1 className="text-3xl leading-tight font-bold text-black">
                パスワードを変更しました
              </h1>
              <p className="mt-6 text-sm leading-6 text-black/70">
                パスワードの変更が完了しました。引き続きサービスをお使いください。
              </p>
              <div className="mt-8">
                <Button asChild color="primary" size="lg" full>
                  <a href="/account/login" className="no-underline">
                    ログインしてください
                  </a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FormLayout>
  );
}
